import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { BaseControlComponent } from './base-control.component';
import { DatePickerComponent } from './date-picker.component';
import { InputComponent } from './input.component';
@Component({
  selector: 'app-range',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    CheckboxModule,
    DatePickerComponent,
    InputComponent,
  ],
  template: `
    <fieldset
      class="tw-pl-0  tw-flex tw-items-center tw-text-[var(--sed-engaged-black-color)] tw-w-full"
    >
      @if (label) {
        <legend
          class="tw-pl-1.5  tw-pr-3 tw-pb-1.5 tw-text-[12px] tw-font-bold tw-text-[var(--sed-black-80-color)]"
        >
          {{ label }}
        </legend>
      } @else if (screenReaderLabel) {
        <legend class="tw-sr-only">{{ screenReaderLabel }}</legend>
      }
      <div class="tw-w-full tw-flex tw-flex-col md:tw-flex-row ">
        @if (type === 'Date') {
          <app-date-picker
            class="tw-w-full md:tw-basis-1/2 md:tw-min-w-[80px]"
            [control]="firstControl"
            [label]="firstLabel"
            [screenReaderLabelPrefix]="label"
            [showHelpText]="showHelpText"
            [tooltip]="firstTooltip"
          />
          <app-date-picker
            class="tw-w-full md:tw-basis-1/2  md:tw-min-w-[80px]"
            [control]="secondControl"
            [label]="secondLabel"
            [screenReaderLabelPrefix]="label"
            [showHelpText]="showHelpText"
            [tooltip]="secondTooltip"
            [minDate]="minDate"
            [maxDate]="maxDate"
            [helpText]="toHelpText"
          />
        } @else if (type === 'Currency') {
          <app-input
            type="Currency"
            class="tw-w-full md:tw-basis-1/2"
            [control]="control"
            label="From"
            [screenReaderLabelPrefix]="label"
            [showHelpText]="showHelpText"
            [tooltip]="tooltip"
            rightIcon="fa-light fa-dollar-sign"
            [currencyLessThanMessage]="
              control.hasError('currencyLessThan')
                ? label +
                  ' From ' +
                  currencyLessThanMessage +
                  ' ' +
                  label +
                  ' To '
                : ''
            "
            [currencyRangeMessage]="
              control.hasError('currencyRange')
                ? label + ' From ' + currencyRangeMessage
                : ''
            "
          />
          <app-input
            type="Currency"
            class="tw-w-full md:tw-basis-1/2"
            [control]="toControl"
            label="To"
            [screenReaderLabelPrefix]="label"
            [showHelpText]="showHelpText"
            [tooltip]="toTooltip"
            rightIcon="fa-light fa-dollar-sign"
            [currencyGreaterThanMessage]="
              toControl.hasError('currencyGreaterThan')
                ? label +
                  ' To ' +
                  currencyGreaterThanMessage +
                  ' ' +
                  label +
                  ' From '
                : ''
            "
            [currencyRangeMessage]="
              toControl.hasError('currencyRange')
                ? label + ' To ' + currencyRangeMessage
                : ''
            "
          />
        } @else if (type === 'Integer') {
          <app-input
            type="Integer"
            class="tw-w-full md:tw-basis-1/2"
            [control]="control"
            label="From"
            [showHelpText]="showHelpText"
            [tooltip]="tooltip"
          />
          <app-input
            type="Integer"
            class="tw-w-full md:tw-basis-1/2"
            [control]="toControl"
            label="To"
            [showHelpText]="showHelpText"
            [tooltip]="toTooltip"
          />
        } @else if (type === 'Decimal') {
          <app-input
            type="Decimal"
            class="tw-w-full md:tw-basis-1/2"
            [control]="control"
            label="From"
            [showHelpText]="showHelpText"
            [tooltip]="tooltip"
          />
          <app-input
            type="Decimal"
            class="tw-w-full md:tw-basis-1/2"
            [control]="toControl"
            label="To"
            [showHelpText]="showHelpText"
            [tooltip]="toTooltip"
          />
        }
      </div>
    </fieldset>
  `,
  styles: ``,
})
export class RangeComponent extends BaseControlComponent implements OnInit {
  @Input({ required: true })
  toControl: FormControl;

  @Input()
  toHelpText: string;

  @Input()
  toTooltip: string;

  @Input()
  type: RangeType = 'Date';

  @Input()
  compact = false;

  @Input()
  fromLabel = 'From';

  @Input()
  toLabel = 'To';

  @Input()
  requireBoth = false;

  @Input()
  requirePair = false;

  @Input()
  currencyGreaterThanMessage = 'must be greater than ';

  @Input()
  currencyLessThanMessage = 'must be less than ';

  @Input()
  currencyRangeMessage = 'must not exceed 15.2 characters';

  @Input()
  reverseOrder = false;

  @Input()
  dateLessThanMessage = '';

  @Input()
  dateGreaterThanMessage = '';

  @Input()
  screenReaderLabel: string;

  /**
   * Maximum number of days allowed between From and To for Date ranges.
   * Set to null to remove the cap for specific screens.
   */
  @Input()
  maxSpanDays: number;

  get firstControl(): FormControl {
    return this.reverseOrder ? this.toControl : this.control;
  }
  get secondControl(): FormControl {
    return this.reverseOrder ? this.control : this.toControl;
  }
  get firstLabel(): string {
    return this.reverseOrder ? this.toLabel : this.fromLabel;
  }
  get secondLabel(): string {
    return this.reverseOrder ? this.fromLabel : this.toLabel;
  }
  get firstTooltip(): string {
    return this.reverseOrder ? this.toTooltip : this.tooltip;
  }
  get secondTooltip(): string {
    return this.reverseOrder ? this.tooltip : this.toTooltip;
  }

  maxDate: Date | null = null;
  minDate: Date;

  private isUpdating = false;

  private updateDateBounds(fromDate: Date | null): void {
    if (this.type !== 'Date') {
      return;
    }

    if (this.maxSpanDays == null) {
      this.maxDate = null;
      return;
    }

    const baseDate =
      fromDate instanceof Date && !isNaN(fromDate.getTime())
        ? new Date(fromDate.getTime())
        : new Date();

    const min = new Date(baseDate.getTime());
    min.setHours(0, 0, 0, 0);
    this.minDate = min;

    const max = new Date(baseDate.getTime());
    max.setDate(max.getDate() + this.maxSpanDays);
    max.setHours(0, 0, 0, 0);
    this.maxDate = max;
  }

  ngOnInit(): void {
    this.updateDateBounds(this.control.value);

    this.dispose.subs = this.control.valueChanges.subscribe(() => {
      if (this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      this.updateDateBounds(this.control.value);
      if (
        (this.requireBoth || this.requirePair) &&
        this.control.value != null
      ) {
        this.toControl.markAsTouched();
      }

      this.toControl.updateValueAndValidity();
      this.isUpdating = false;
    });

    this.dispose.subs = this.toControl.valueChanges.subscribe(() => {
      if (this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      if (
        (this.requireBoth || this.requirePair) &&
        this.toControl.value != null
      ) {
        this.control.markAsTouched();
      }

      this.control.updateValueAndValidity();
      this.isUpdating = false;
    });

    if (this.requireBoth) {
      this.control.addValidators([
        Validators.required,
        ValidatorsEx.requirePairedValue(this.toControl),
      ]);
      this.toControl.addValidators([
        Validators.required,
        ValidatorsEx.requirePairedValue(this.control),
      ]);
    }

    if (this.requirePair) {
      this.control.addValidators(
        ValidatorsEx.requirePairedValue(this.toControl),
      );
      this.toControl.addValidators(
        ValidatorsEx.requirePairedValue(this.control),
      );
    }

    switch (this.type) {
      case 'Integer':
        this.toControl.addValidators(
          ValidatorsEx.integerGreaterThan(this.control),
        );
        this.control.addValidators(
          ValidatorsEx.integerLessThan(this.toControl),
        );
        break;
      case 'Decimal':
        this.toControl.addValidators(
          ValidatorsEx.decimalGreaterThan(this.control),
        );
        this.control.addValidators(
          ValidatorsEx.decimalLessThan(this.toControl),
        );
        break;
      case 'Date':
        this.toControl.addValidators(
          ValidatorsEx.dateGreaterThan(
            this.control,
            false,
            this.dateGreaterThanMessage || undefined,
          ),
        );
        this.control.addValidators(
          ValidatorsEx.dateLessThan(
            this.toControl,
            false,
            this.dateLessThanMessage || undefined,
          ),
        );
        break;
      case 'Currency':
        this.toControl.addValidators(
          ValidatorsEx.currencyGreaterThan(this.control),
        );
        this.control.addValidators(
          ValidatorsEx.currencyLessThan(this.toControl),
        );
        this.control.addValidators(ValidatorsEx.currencyRange(this.control));
        this.toControl.addValidators(
          ValidatorsEx.currencyRange(this.toControl),
        );
        break;
    }
  }

  override ngOnDestroy(): void {
    if (this.requireBoth) {
      this.control.removeValidators(Validators.required);
      this.toControl.removeValidators(Validators.required);
    }
    if (this.requireBoth || this.requirePair) {
      this.control.updateValueAndValidity({ emitEvent: false });
      this.toControl.updateValueAndValidity({ emitEvent: false });
    }
    super.ngOnDestroy();
  }
}

export type RangeType = 'Date' | 'Currency' | 'Integer' | 'Decimal';
